import React from 'react';
import { Info, Heart, Activity, BarChart2, Thermometer, BookOpen, Clock } from 'lucide-react';

/**
 * Компонент с информацией о максимальном пульсе, тренировочных зонах и рекомендациях
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-red-500" />
        Максимальный пульс и зоны тренировок: полное руководство
      </h2>
      
      <div className="prose prose-red dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Что такое максимальный пульс?
        </h3>
        <p>
          <strong>Максимальный пульс (Maximum Heart Rate, MHR)</strong> — это наивысшая частота сердечных сокращений, которую может достичь человек во время максимальной физической нагрузки. Этот показатель индивидуален и зависит от многих факторов, главным из которых является возраст. С годами максимальный пульс естественным образом снижается.
        </p>
        <p>
          Точное измерение максимального пульса возможно только в лабораторных условиях при проведении нагрузочного теста под наблюдением специалистов. Однако для практических целей используются различные формулы расчета, которые дают достаточно точные приближения для большинства людей.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-red-500" />
          Формулы расчета MHR
        </h3>
        <p>
          Существует несколько формул для расчета максимального пульса, каждая из которых имеет свои особенности и область применения:
        </p>
        <ul>
          <li>
            <strong>Формула Танаки (2001)</strong>: 208 - (0.7 × возраст). Считается одной из наиболее точных и современных формул для общего населения. Эта формула была получена на основе мета-анализа 351 исследования с участием более 18 000 человек.
          </li>
          <li>
            <strong>Формула Хаскелла-Фокса (1970)</strong>: 220 - возраст. Самая известная и простая формула, но имеет наибольшую погрешность. Несмотря на популярность, современные исследования показывают, что эта формула часто недооценивает MHR у пожилых людей и переоценивает у молодых.
          </li>
          <li>
            <strong>Формула Геллиша (2007)</strong>: 207 - (0.7 × возраст). Близка к формуле Танаки и хорошо работает для большинства возрастных групп.
          </li>
          <li>
            <strong>Формула Гулати (2010)</strong>: 206 - (0.88 × возраст). Разработана специально для женщин на основе исследования с участием более 5000 женщин.
          </li>
        </ul>
        <p>
          В России специалисты ФНЦ ВНИИФК часто используют формулу 211 - (0.64 × возраст), которая учитывает особенности российской популяции и климатических условий.
        </p>

        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800 my-6">
          <h4 className="text-red-700 dark:text-red-300 font-medium">Важно помнить!</h4>
          <p className="text-red-600 dark:text-red-400 text-sm mb-0">
            Все формулы дают лишь приблизительную оценку максимального пульса с погрешностью до ±10-15 ударов в минуту. Многие факторы, включая генетику, уровень тренированности, вес, наличие заболеваний и даже географию проживания, могут влиять на ваш индивидуальный максимальный пульс.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-red-500" />
          Зоны пульса и их значение
        </h3>
        <p>
          Зоны пульса — это диапазоны частоты сердечных сокращений, выраженные в процентах от максимального пульса. Тренировки в разных зонах имеют различные физиологические эффекты и помогают достичь разных целей:
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th>Зона</th>
                <th>% от MHR</th>
                <th>Название</th>
                <th>Эффект тренировок</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Зона 1</td>
                <td>50-60%</td>
                <td>Восстановительная</td>
                <td>Активное восстановление, разминка, укрепление здоровья начинающих</td>
              </tr>
              <tr>
                <td>Зона 2</td>
                <td>60-70%</td>
                <td>Легкая аэробная</td>
                <td>Базовое сжигание жира, развитие аэробной выносливости</td>
              </tr>
              <tr>
                <td>Зона 3</td>
                <td>70-80%</td>
                <td>Аэробная</td>
                <td>Улучшение сердечно-сосудистой системы, повышение эффективности тренировок</td>
              </tr>
              <tr>
                <td>Зона 4</td>
                <td>80-90%</td>
                <td>Анаэробная</td>
                <td>Улучшение анаэробного порога, повышение скоростных качеств</td>
              </tr>
              <tr>
                <td>Зона 5</td>
                <td>90-100%</td>
                <td>Максимальная</td>
                <td>Развитие максимальной производительности, скорости и силы</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-red-500" />
          Метод Карвонена для расчета зон пульса
        </h3>
        <p>
          Метод Карвонена (формула резерва сердечного ритма) считается более точным для расчета тренировочных зон, так как учитывает не только максимальный пульс, но и пульс в состоянии покоя. Это особенно важно для людей с низким или высоким пульсом покоя.
        </p>
        <p>
          <strong>Формула Карвонена</strong>: Целевой пульс = Пульс покоя + (Коэффициент интенсивности × (Максимальный пульс - Пульс покоя))
        </p>
        <p>
          Где коэффициент интенсивности — это процент от вашего резерва сердечного ритма (разница между максимальным пульсом и пульсом покоя). Данный метод позволяет более точно настроить интенсивность тренировок под индивидуальные особенности организма.
        </p>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Как измерить пульс в покое
          </h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            Для правильного измерения пульса в покое необходимо:
          </p>
          <ol className="text-green-600 dark:text-green-400 text-sm mb-0">
            <li>Измерять утром, сразу после пробуждения, лежа в постели</li>
            <li>Не употреблять кофеин и не курить минимум за 4 часа до измерения</li>
            <li>Находиться в спокойном состоянии минимум 10 минут перед измерением</li>
            <li>Сделать несколько измерений (в течение 3-5 дней) и взять среднее значение</li>
          </ol>
        </div>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-red-500" />
          Особенности тренировок в разных зонах пульса
        </h3>
        
        <h4>Зона 1: Восстановительная (50-60% MHR)</h4>
        <p>
          Это зона активного восстановления между интенсивными тренировками. Тренировки в этой зоне практически не утомляют организм и могут выполняться ежедневно. Они улучшают капилляризацию мышц и способствуют выведению продуктов метаболизма после тяжелых нагрузок.
        </p>
        <ul>
          <li><strong>Примеры активностей</strong>: легкая ходьба, медленное плавание, стретчинг, йога</li>
          <li><strong>Длительность</strong>: 30-60 минут</li>
          <li><strong>Частота</strong>: ежедневно или между интенсивными тренировками</li>
        </ul>
        
        <h4>Зона 2: Легкая аэробная (60-70% MHR)</h4>
        <p>
          Оптимальная зона для длительных тренировок на выносливость и сжигание жира. В этой зоне организм использует преимущественно жировые запасы в качестве источника энергии. Тренировки легкие и комфортные, можно поддерживать разговор.
        </p>
        <ul>
          <li><strong>Примеры активностей</strong>: быстрая ходьба, легкий бег, велосипед в спокойном темпе, плавание</li>
          <li><strong>Длительность</strong>: 45-90 минут</li>
          <li><strong>Частота</strong>: 3-5 раз в неделю</li>
        </ul>
        
        <h4>Зона 3: Аэробная (70-80% MHR)</h4>
        <p>
          В этой зоне улучшается работа сердечно-сосудистой системы, повышается способность мышц утилизировать кислород. Тренировки умеренно интенсивные, вы можете произносить короткие фразы, но поддерживать полноценный разговор уже сложно.
        </p>
        <ul>
          <li><strong>Примеры активностей</strong>: бег в среднем темпе, интенсивная езда на велосипеде, плавание в хорошем темпе</li>
          <li><strong>Длительность</strong>: 20-60 минут</li>
          <li><strong>Частота</strong>: 2-3 раза в неделю</li>
        </ul>
        
        <h4>Зона 4: Анаэробная (80-90% MHR)</h4>
        <p>
          Высокоинтенсивные тренировки в этой зоне значительно повышают анаэробный порог и способность переносить высокие нагрузки. Организм работает на пределе аэробных возможностей, накапливается молочная кислота. Говорить в этой зоне очень сложно.
        </p>
        <ul>
          <li><strong>Примеры активностей</strong>: интервальный бег, темповые тренировки, HIIT</li>
          <li><strong>Длительность</strong>: 15-30 минут (включая интервалы отдыха)</li>
          <li><strong>Частота</strong>: 1-2 раза в неделю</li>
        </ul>
        
        <h4>Зона 5: Максимальная (90-100% MHR)</h4>
        <p>
          Тренировки на пике возможностей, организм работает в анаэробном режиме. Используется для развития максимальной мощности и скорости. В этой зоне можно находиться очень короткое время.
        </p>
        <ul>
          <li><strong>Примеры активностей</strong>: спринты, максимальные ускорения, очень короткие интенсивные интервалы</li>
          <li><strong>Длительность</strong>: до 3-5 минут суммарно за тренировку (в виде коротких интервалов)</li>
          <li><strong>Частота</strong>: не более 1 раза в неделю</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-red-500" />
          Распределение тренировок по зонам пульса
        </h3>
        <p>
          Оптимальное распределение тренировок по зонам пульса зависит от ваших целей, уровня подготовки и вида спорта. Однако, существуют общие рекомендации:
        </p>
        <ul>
          <li><strong>Для общего оздоровления и снижения веса</strong>: 70-80% в зонах 1-2, 15-20% в зоне 3, 5-10% в зонах 4-5</li>
          <li><strong>Для улучшения выносливости</strong>: 60-70% в зонах 1-2, 20-30% в зоне 3, 10% в зонах 4-5</li>
          <li><strong>Для повышения скоростных качеств</strong>: 50-60% в зонах 1-2, 20% в зоне 3, 20-30% в зонах 4-5</li>
        </ul>
        <p>
          Модель поляризованных тренировок, популярная среди профессиональных спортсменов, предполагает сосредоточение большинства тренировок в зонах 1-2 и 4-5, с минимальным временем в зоне 3.
        </p>

        <h3>Значение тренировок по пульсу в России</h3>
        <p>
          В России тренировки с контролем пульса становятся все более популярными благодаря развитию фитнес-индустрии и доступности пульсометров. Российская школа спортивной медицины имеет богатые традиции в использовании контроля пульса для оптимизации тренировочного процесса.
        </p>
        <p>
          Особенности российского подхода включают:
        </p>
        <ul>
          <li>Учет климатических особенностей (холодные зимы могут влиять на пульс)</li>
          <li>Акцент на восстановительных процедурах после интенсивных тренировок (баня, контрастный душ)</li>
          <li>Адаптация методик под российский образ жизни и питания</li>
          <li>Учет специфики популярных в России видов спорта (лыжные гонки, хоккей, фигурное катание)</li>
        </ul>
        <p>
          Институт медико-биологических проблем РАН и ФНЦ ВНИИФК регулярно проводят исследования в области физиологии нагрузок и оптимизации тренировочных процессов для российских спортсменов и любителей фитнеса.
        </p>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            Меры предосторожности
          </h4>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-2">
            Тренировки с контролем пульса требуют внимательности и осторожности, особенно если у вас есть заболевания сердечно-сосудистой системы. Перед началом тренировок проконсультируйтесь с врачом если:
          </p>
                      <ul className="text-amber-600 dark:text-amber-400 text-sm mb-0 list-disc list-inside">
            <li>Вам более 40 лет и вы раньше не тренировались регулярно</li>
            <li>У вас есть заболевания сердца, лёгких, почек или другие хронические заболевания</li>
            <li>У вас повышенное давление</li>
            <li>Вы принимаете лекарства, влияющие на сердечный ритм</li>
            <li>Во время тренировок вы испытываете головокружение, одышку или боль в груди</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Выбор и использование пульсометра
        </h3>
        <p>
          Для точного контроля пульса во время тренировок рекомендуется использовать пульсометр. На российском рынке доступны различные модели, от простых нагрудных датчиков до многофункциональных спортивных часов.
        </p>
        <h4>Типы пульсометров:</h4>
        <ul>
          <li><strong>Нагрудные датчики</strong> — наиболее точные, особенно во время интенсивных нагрузок</li>
          <li><strong>Спортивные часы с оптическим датчиком</strong> — удобные для повседневного ношения, но могут быть менее точными при интенсивных тренировках</li>
          <li><strong>Фитнес-браслеты</strong> — доступные по цене, но часто имеют ограниченную точность</li>
          <li><strong>Кардиотренажеры с встроенными датчиками</strong> — удобны для тренировок в помещении, но точность зависит от модели</li>
        </ul>
        <p>
          При выборе пульсометра обращайте внимание на точность измерений, удобство использования, совместимость с вашими устройствами, срок службы батареи и дополнительные функции (например, GPS или анализ вариабельности сердечного ритма).
        </p>

        <p className="font-semibold mt-6">
          Тренировки с контролем пульса — это научный подход к физической активности, который помогает достичь максимальных результатов без перетренированности и снижает риск травм. Используйте наш калькулятор для определения ваших индивидуальных зон пульса и начните тренироваться эффективнее!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;